import type { AvailabilityResult } from './bookingFlowAnalyticsTypes.ts';

export interface BookingFlowIdentityStorageAdapter {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface BookingFlowCanonicalSelection {
  requestedDateKey?: string;
  headcount?: number;
  startTimeBucket?: string;
  durationMinutes?: number;
}

export interface BookingFlowAvailabilityResultSnapshot {
  availabilityDecisionId: string;
  selectionRevision: number;
  requestedDateKey: string;
  startTimeBucket?: string;
  headcount: number;
  durationMinutes?: number;
  availabilityResult: AvailabilityResult;
}

export interface BookingFlowIdentityState {
  unitId: string;
  sessionId: string;
  bookingFlowId: string;
  selectionRevision: number;
  canonicalSelection: BookingFlowCanonicalSelection;
  availabilityRevision?: number;
  lastAvailabilityResult?: BookingFlowAvailabilityResultSnapshot;
  activeSubmitAttemptId?: string;
  contactFormStartedEmitted?: boolean;
}

export interface BookingFlowIdentityEngineOptions {
  storage: BookingFlowIdentityStorageAdapter;
  createId: () => string;
}

export interface BookingFlowScope {
  unitId: string;
  sessionId: string;
}

export interface BookingFlowSubmitAttemptResult {
  state: BookingFlowIdentityState;
  submitAttemptId: string;
}

const STORAGE_KEY_PREFIX = 'booking_flow_analytics_identity:v1';
const IDENTIFIER = /^[A-Za-z0-9][A-Za-z0-9:_-]{0,127}$/;
const DATE_KEY = /^\d{4}-\d{2}-\d{2}$/;
const TIME_BUCKET = /^\d{2}:\d{2}$/;
const AVAILABILITY_RESULTS = new Set<AvailabilityResult>([
  'available',
  'unavailable',
  'outside_bookable_window',
  'capacity_full',
]);
const STATE_KEYS = new Set([
  'unitId',
  'sessionId',
  'bookingFlowId',
  'selectionRevision',
  'canonicalSelection',
  'availabilityRevision',
  'lastAvailabilityResult',
  'activeSubmitAttemptId',
  'contactFormStartedEmitted',
]);
const SELECTION_KEYS = new Set(['requestedDateKey', 'headcount', 'startTimeBucket', 'durationMinutes']);
const AVAILABILITY_KEYS = new Set([
  'availabilityDecisionId',
  'selectionRevision',
  'requestedDateKey',
  'startTimeBucket',
  'headcount',
  'durationMinutes',
  'availabilityResult',
]);

const storageKey = ({ unitId, sessionId }: BookingFlowScope) => `${STORAGE_KEY_PREFIX}:${encodeURIComponent(unitId)}:${encodeURIComponent(sessionId)}`;
const isRecord = (value: unknown): value is Record<string, unknown> => Boolean(value) && typeof value === 'object' && !Array.isArray(value);
const isIdentifier = (value: unknown) => typeof value === 'string' && IDENTIFIER.test(value);
const isDateKey = (value: unknown) => {
  if (typeof value !== 'string' || !DATE_KEY.test(value)) return false;
  const parsed = new Date(`${value}T00:00:00.000Z`);
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
};
const isHeadcount = (value: unknown) => Number.isInteger(value) && Number(value) >= 1 && Number(value) <= 99;
const isTimeBucket = (value: unknown) => typeof value === 'string' && TIME_BUCKET.test(value) && Number(value.slice(0, 2)) <= 23 && Number(value.slice(3, 5)) <= 59;
const isPositiveInteger = (value: unknown) => Number.isSafeInteger(value) && Number(value) > 0;
const isDurationMinutes = (value: unknown) => Number.isSafeInteger(value) && Number(value) >= 1 && Number(value) <= 24 * 60;
const incrementRevision = (value: number) => {
  if (!Number.isSafeInteger(value) || value >= Number.MAX_SAFE_INTEGER) {
    throw new Error('BOOKING_FLOW_ANALYTICS_REVISION_OVERFLOW');
  }
  return value + 1;
};

const hasOnlyKeys = (record: Record<string, unknown>, keys: Set<string>) => Object.keys(record).every(key => keys.has(key));

const normalizeSelection = (selection: BookingFlowCanonicalSelection): BookingFlowCanonicalSelection => {
  const normalized: BookingFlowCanonicalSelection = {};
  if (selection.requestedDateKey !== undefined) normalized.requestedDateKey = selection.requestedDateKey;
  if (selection.headcount !== undefined) normalized.headcount = selection.headcount;
  if (selection.startTimeBucket !== undefined) normalized.startTimeBucket = selection.startTimeBucket;
  if (selection.durationMinutes !== undefined) normalized.durationMinutes = selection.durationMinutes;
  return normalized;
};

const normalizeAvailability = (result: BookingFlowAvailabilityResultSnapshot): BookingFlowAvailabilityResultSnapshot => {
  const normalized: BookingFlowAvailabilityResultSnapshot = {
    availabilityDecisionId: result.availabilityDecisionId,
    selectionRevision: result.selectionRevision,
    requestedDateKey: result.requestedDateKey,
    headcount: result.headcount,
    availabilityResult: result.availabilityResult,
  };
  if (result.durationMinutes !== undefined) normalized.durationMinutes = result.durationMinutes;
  if (result.startTimeBucket !== undefined) normalized.startTimeBucket = result.startTimeBucket;
  return normalized;
};

const selectionsEqual = (left: BookingFlowCanonicalSelection, right: BookingFlowCanonicalSelection) => (
  left.requestedDateKey === right.requestedDateKey
  && left.headcount === right.headcount
  && left.startTimeBucket === right.startTimeBucket
  && left.durationMinutes === right.durationMinutes
);

const availabilityEqual = (left: BookingFlowAvailabilityResultSnapshot | undefined, right: BookingFlowAvailabilityResultSnapshot) => Boolean(left)
  // A repeated backend check may mint a new proof ID for the same stable result.
  // Logical observation identity is the selection/result revision, not the proof document ID.
  && left?.selectionRevision === right.selectionRevision
  && left?.requestedDateKey === right.requestedDateKey
  && left?.startTimeBucket === right.startTimeBucket
  && left?.headcount === right.headcount
  && left?.durationMinutes === right.durationMinutes
  && left?.availabilityResult === right.availabilityResult;

const availabilityMatchesSelection = (state: Pick<BookingFlowIdentityState, 'selectionRevision' | 'canonicalSelection'>, result: BookingFlowAvailabilityResultSnapshot) => (
  state.selectionRevision === result.selectionRevision
  && state.canonicalSelection.requestedDateKey === result.requestedDateKey
  && state.canonicalSelection.headcount === result.headcount
  && state.canonicalSelection.durationMinutes === result.durationMinutes
  && state.canonicalSelection.startTimeBucket === result.startTimeBucket
);

const isEmptySelection = (selection: BookingFlowCanonicalSelection) => (
  selection.requestedDateKey === undefined
  && selection.headcount === undefined
  && selection.durationMinutes === undefined
  && selection.startTimeBucket === undefined
);

const isValidSelection = (selection: unknown): selection is BookingFlowCanonicalSelection => {
  if (!isRecord(selection) || !hasOnlyKeys(selection, SELECTION_KEYS)) return false;
  if ('requestedDateKey' in selection && !isDateKey(selection.requestedDateKey)) return false;
  if ('headcount' in selection && !isHeadcount(selection.headcount)) return false;
  if ('startTimeBucket' in selection && !isTimeBucket(selection.startTimeBucket)) return false;
  if ('durationMinutes' in selection && !isDurationMinutes(selection.durationMinutes)) return false;
  return true;
};

const isValidAvailability = (availability: unknown): availability is BookingFlowAvailabilityResultSnapshot => {
  if (!isRecord(availability) || !hasOnlyKeys(availability, AVAILABILITY_KEYS)) return false;
  if (!isIdentifier(availability.availabilityDecisionId)) return false;
  if (!isPositiveInteger(availability.selectionRevision)) return false;
  if (!isDateKey(availability.requestedDateKey)) return false;
  if ('startTimeBucket' in availability && !isTimeBucket(availability.startTimeBucket)) return false;
  if (!isHeadcount(availability.headcount)) return false;
  if ('durationMinutes' in availability && !isDurationMinutes(availability.durationMinutes)) return false;
  return typeof availability.availabilityResult === 'string' && AVAILABILITY_RESULTS.has(availability.availabilityResult as AvailabilityResult);
};

const isValidState = (value: unknown, scope: BookingFlowScope): value is BookingFlowIdentityState => {
  if (!isRecord(value) || !hasOnlyKeys(value, STATE_KEYS)) return false;
  if (value.unitId !== scope.unitId || value.sessionId !== scope.sessionId) return false;
  if (!isIdentifier(value.bookingFlowId)) return false;
  if (!isPositiveInteger(value.selectionRevision)) return false;
  if (!isValidSelection(value.canonicalSelection)) return false;
  if ('availabilityRevision' in value && !isPositiveInteger(value.availabilityRevision)) return false;
  if ('lastAvailabilityResult' in value && !isValidAvailability(value.lastAvailabilityResult)) return false;
  if (('availabilityRevision' in value) !== ('lastAvailabilityResult' in value)) return false;
  if ('lastAvailabilityResult' in value && !availabilityMatchesSelection(value as unknown as BookingFlowIdentityState, value.lastAvailabilityResult as BookingFlowAvailabilityResultSnapshot)) return false;
  if ('activeSubmitAttemptId' in value && !isIdentifier(value.activeSubmitAttemptId)) return false;
  if ('contactFormStartedEmitted' in value && typeof value.contactFormStartedEmitted !== 'boolean') return false;
  return true;
};

const cloneState = (state: BookingFlowIdentityState): BookingFlowIdentityState => ({
  unitId: state.unitId,
  sessionId: state.sessionId,
  bookingFlowId: state.bookingFlowId,
  selectionRevision: state.selectionRevision,
  canonicalSelection: { ...state.canonicalSelection },
  ...(state.availabilityRevision !== undefined ? { availabilityRevision: state.availabilityRevision } : {}),
  ...(state.lastAvailabilityResult ? { lastAvailabilityResult: { ...state.lastAvailabilityResult } } : {}),
  ...(state.activeSubmitAttemptId ? { activeSubmitAttemptId: state.activeSubmitAttemptId } : {}),
  ...(state.contactFormStartedEmitted ? { contactFormStartedEmitted: true } : {}),
});

export const createBookingFlowIdentityEngine = ({ storage, createId }: BookingFlowIdentityEngineOptions) => {
  const readState = (scope: BookingFlowScope): BookingFlowIdentityState | null => {
    const key = storageKey(scope);
    try {
      const raw = storage.getItem(key);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as unknown;
      if (!isValidState(parsed, scope)) {
        storage.removeItem(key);
        return null;
      }
      return cloneState(parsed);
    } catch {
      storage.removeItem(key);
      return null;
    }
  };

  const writeState = (state: BookingFlowIdentityState) => {
    storage.setItem(storageKey(state), JSON.stringify(state));
    return cloneState(state);
  };

  const closeFlow = (scope: BookingFlowScope) => {
    storage.removeItem(storageKey(scope));
  };

  const createFlow = (scope: BookingFlowScope): BookingFlowIdentityState => writeState({
    unitId: scope.unitId,
    sessionId: scope.sessionId,
    bookingFlowId: createId(),
    selectionRevision: 1,
    canonicalSelection: {},
  });

  const getOrCreateFlow = (scope: BookingFlowScope): BookingFlowIdentityState => readState(scope) ?? createFlow(scope);

  const startNewFlow = (scope: BookingFlowScope): BookingFlowIdentityState => {
    closeFlow(scope);
    return createFlow(scope);
  };

  const updateSelection = (scope: BookingFlowScope, selection: BookingFlowCanonicalSelection): BookingFlowIdentityState => {
    if (!isValidSelection(selection)) {
      throw new Error('BOOKING_FLOW_ANALYTICS_SELECTION_INVALID');
    }
    const state = getOrCreateFlow(scope);
    const canonicalSelection = normalizeSelection(selection);
    if (selectionsEqual(state.canonicalSelection, canonicalSelection)) return state;
    return writeState({
      unitId: state.unitId,
      sessionId: state.sessionId,
      bookingFlowId: state.bookingFlowId,
      selectionRevision: isEmptySelection(state.canonicalSelection)
        ? state.selectionRevision
        : incrementRevision(state.selectionRevision),
      canonicalSelection,
      ...(state.contactFormStartedEmitted ? { contactFormStartedEmitted: true } : {}),
    });
  };

  const recordAvailabilityResult = (scope: BookingFlowScope, result: BookingFlowAvailabilityResultSnapshot): BookingFlowIdentityState => {
    if (!isValidAvailability(result)) {
      throw new Error('BOOKING_FLOW_ANALYTICS_AVAILABILITY_INVALID');
    }
    const state = getOrCreateFlow(scope);
    const lastAvailabilityResult = normalizeAvailability(result);
    if (!availabilityMatchesSelection(state, lastAvailabilityResult)) return state;
    if (availabilityEqual(state.lastAvailabilityResult, lastAvailabilityResult)) return state;
    return writeState({
      ...state,
      availabilityRevision: state.availabilityRevision === undefined
        ? 1
        : incrementRevision(state.availabilityRevision),
      lastAvailabilityResult,
    });
  };

  const markContactFormStarted = (scope: BookingFlowScope): BookingFlowIdentityState => {
    const state = getOrCreateFlow(scope);
    if (state.contactFormStartedEmitted) return state;
    return writeState({ ...state, contactFormStartedEmitted: true });
  };

  const getOrCreateSubmitAttempt = (scope: BookingFlowScope): BookingFlowSubmitAttemptResult => {
    const state = getOrCreateFlow(scope);
    if (state.activeSubmitAttemptId) return { state, submitAttemptId: state.activeSubmitAttemptId };
    const activeSubmitAttemptId = createId();
    const next = writeState({ ...state, activeSubmitAttemptId });
    return { state: next, submitAttemptId: activeSubmitAttemptId };
  };

  const markValidationBlocked = (scope: BookingFlowScope): BookingFlowIdentityState | null => {
    const state = readState(scope);
    return state ? writeState(state) : null;
  };

  const markSubmitTransportFailed = (scope: BookingFlowScope, submitAttemptId: string): BookingFlowIdentityState | null => {
    const state = readState(scope);
    if (!state || state.activeSubmitAttemptId !== submitAttemptId) return state;
    return writeState(state);
  };

  const markSubmitRejected = (scope: BookingFlowScope, submitAttemptId: string): BookingFlowIdentityState | null => {
    const state = readState(scope);
    if (!state || state.activeSubmitAttemptId !== submitAttemptId) return state;
    const { activeSubmitAttemptId: _activeSubmitAttemptId, ...next } = state;
    return writeState(next);
  };

  const markSubmitCreated = (scope: BookingFlowScope, submitAttemptId: string): BookingFlowIdentityState | null => {
    const state = readState(scope);
    if (!state || state.activeSubmitAttemptId !== submitAttemptId) return state;
    closeFlow(scope);
    return null;
  };

  return {
    getState: readState,
    getOrCreateFlow,
    startNewFlow,
    closeFlow,
    updateSelection,
    recordAvailabilityResult,
    markContactFormStarted,
    getOrCreateSubmitAttempt,
    markValidationBlocked,
    markSubmitTransportFailed,
    markSubmitRejected,
    markSubmitCreated,
  };
};
